// Purpose: Custom hook for video autoplay with intersection observer
// Audience: Components that need smart video autoplay (like UniversalEvidenceWindow)
// Features:
// - Intersection Observer for visibility-based autoplay
// - Play once behavior (videos don't replay on scroll back)
// - Auto-pause when scrolled away (< 50% visible)
// - Integration with VideoPlaybackContext for state tracking
// - Automatic cleanup on unmount
// - Configurable visibility thresholds

import { useEffect, useRef, useState, useCallback } from 'react';
import { useVideoPlayback } from '../contexts/VideoPlaybackContext';

/**
 * Custom hook for managing video autoplay with visibility detection
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.videoId - Unique identifier for this video
 * @param {boolean} options.enabled - Whether autoplay is enabled (default: true)
 * @param {number} options.visibilityThreshold - Intersection ratio threshold for autoplay (default: 0.6)
 * @param {Object} options.metadata - Optional metadata to store with the video
 * @returns {Object} Returns { videoRef, containerRef, isPlaying, hasEnded, restart, getVideoProps, getContainerProps }
 */
export const useVideoAutoplay = ({
  videoId,
  enabled = true,
  visibilityThreshold = 0.6,
  metadata = {}
} = {}) => {
  const { registerVideo, unregisterVideo, isVideoPlaying } = useVideoPlayback();
  
  // Refs for video element and container
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  
  // State for visibility and playback
  const [hasEnded, setHasEnded] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false); // Track if video has been autoplayed once
  const [pageHasInteraction, setPageHasInteraction] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Track visibility state

  // Register/unregister video with context
  useEffect(() => {
    if (!videoId || !videoRef.current) return;

    registerVideo(videoId, videoRef.current, metadata);

    return () => {
      unregisterVideo(videoId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, registerVideo, unregisterVideo]); // metadata is stable, don't need in deps

  // Detect any page interaction to enable autoplay
  useEffect(() => {
    const markInteraction = () => {
      setPageHasInteraction(true);
    };

    // Listen for any user interaction on the page
    const events = ['click', 'touchstart', 'scroll', 'keydown'];
    events.forEach(evt => {
      document.addEventListener(evt, markInteraction, { once: true, passive: true });
    });

    return () => {
      events.forEach(evt => {
        document.removeEventListener(evt, markInteraction);
      });
    };
  }, []);


  // Intersection Observer for visibility detection
  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Different thresholds for play vs pause
          const shouldStartPlaying = entry.isIntersecting && entry.intersectionRatio >= visibilityThreshold; // Need 60% visible to start
          const shouldKeepPlaying = entry.isIntersecting && entry.intersectionRatio >= 0.5; // Need 50% visible to keep playing
          
          setIsVisible(shouldStartPlaying); // Track visibility state
          
          if (videoRef.current) {
            const video = videoRef.current;
            
            if (shouldStartPlaying) {
              // Video scrolled into view (60%+ visible)
              // Reset hasAutoPlayed when video becomes visible again after being out of view
              if (!isVisible && hasAutoPlayed && video.paused) {
                console.log('🔄 Resetting autoplay state for', videoId, '- video came back into view');
                setHasAutoPlayed(false);
                setHasEnded(false);
                video.currentTime = 0; // Reset to beginning for fresh playback
              }
              
              if (!hasEnded && !hasAutoPlayed && video.paused) {
                console.log('🎬 Attempting autoplay for', videoId);
                
                // Small delay to ensure video element is ready
                setTimeout(() => {
                  if (!videoRef.current || !videoRef.current.paused) return;
                  
                  // Try to autoplay
                  const playPromise = videoRef.current.play();
                  if (playPromise !== undefined) {
                    playPromise
                      .then(() => {
                        console.log('✅ Autoplay successful for', videoId);
                        setHasAutoPlayed(true);
                        setPageHasInteraction(true);
                      })
                      .catch(err => {
                        console.log('❌ Autoplay blocked for', videoId, ':', err.message);
                        // Don't set hasAutoPlayed - let user manually start it
                      });
                  }
                }, 100); // 100ms delay for element readiness
              } else {
                // Log why we're not attempting autoplay
                if (hasEnded) console.log('⏭️ Skipping', videoId, '- video has ended');
                if (hasAutoPlayed && !video.paused) console.log('⏭️ Skipping', videoId, '- already playing');
              }
            } else if (!shouldKeepPlaying) {
              // Video scrolled away (less than 50% visible) - pause it
              if (!video.paused) {
                console.log('⏸️ Auto-pausing video (scrolled away):', videoId);
                video.pause();
                // Reset autoplay state when video goes out of view so it can play again when scrolled back
                setHasAutoPlayed(false);
                setHasEnded(false);
              }
            }
            // Video stays playing only when 50%+ visible
          }
        });
      },
      { 
        threshold: [0, 0.5, visibilityThreshold, 1], // Observe at: 0%, 50%, 60%, 100% visibility
        rootMargin: '0px' // No margin - only act when actually visible
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [enabled, visibilityThreshold, hasEnded, hasAutoPlayed, videoId, pageHasInteraction, isVisible]);

  // Note: Autoplay is handled directly in the Intersection Observer above
  // This avoids conflicts and ensures immediate response to visibility changes
  // The old shouldPlay effect is removed to prevent dual autoplay mechanisms

  // Video event handlers
  const handleLoadedData = useCallback(() => {
    // Video metadata loaded - needed for video element
  }, []);

  const handleEnded = useCallback(() => {
    setHasEnded(true);
    setHasAutoPlayed(true); // Mark as having played once
  }, []);

  const handlePlay = useCallback(() => {
    // Mark as having been autoplayed when video starts playing (from any source)
    if (!hasAutoPlayed) {
      console.log('Video started playing, marking as autoplayed:', videoId);
      setHasAutoPlayed(true);
    }
  }, [hasAutoPlayed, videoId]);

  const handlePause = useCallback(() => {
    // Empty but needed for video element event binding
  }, []);

  const handleError = useCallback((error) => {
    console.warn(`Video ${videoId} error:`, error);
  }, [videoId]);

  // Restart control (for replay overlay)
  const restart = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setHasEnded(false);
      setHasAutoPlayed(false); // Reset so video can autoplay again
      // Manual replay always plays
      videoRef.current.play().catch(err => {
        console.warn('Restart play failed:', err);
      });
    }
  }, []);

  // Get video element props to spread onto video element
  const getVideoProps = useCallback(() => ({
    ref: videoRef,
    onLoadedData: handleLoadedData,
    onEnded: handleEnded,
    onPlay: handlePlay,
    onPause: handlePause,
    onError: handleError,
    muted: true,
    playsInline: true,
    'webkit-playsinline': 'true', // For older iOS devices
    preload: 'metadata'
  }), [handleLoadedData, handleEnded, handlePlay, handlePause, handleError]);

  // Get container props to spread onto container element
  const getContainerProps = useCallback(() => ({
    ref: containerRef
  }), []);

  // Track if video is actually playing
  const [isActuallyPlaying, setIsActuallyPlaying] = useState(false);
  
  // Update playing state based on video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlayEvent = () => setIsActuallyPlaying(true);
    const handlePauseEvent = () => setIsActuallyPlaying(false);

    video.addEventListener('play', handlePlayEvent);
    video.addEventListener('playing', handlePlayEvent);
    video.addEventListener('pause', handlePauseEvent);

    return () => {
      video.removeEventListener('play', handlePlayEvent);
      video.removeEventListener('playing', handlePlayEvent);
      video.removeEventListener('pause', handlePauseEvent);
    };
  }, []);

  return {
    // Refs
    videoRef,
    containerRef,
    
    // State
    isPlaying: isActuallyPlaying || isVideoPlaying(videoId), // Either from DOM events or context
    hasEnded,
    
    // Controls
    restart,
    
    // Props helpers
    getVideoProps,
    getContainerProps
  };
};

export default useVideoAutoplay;
